/**
 * Clickout Service
 * Handles clickout event reporting to the backend.
 * This is a write-only service - we only send clickout events for backend processing.
 * The UI doesn't store, retrieve, or manage clickout data.
 * 
 * @module clickoutService
 */

import api from '../utils/apiv2';

/**
 * Clickout Service class
 * Manages clickout event reporting to backend analytics
 */
class ClickoutService {
  /**
   * Record a clickout event
   * @param {string} userId - User ID from JWT token
   * @param {string} searchKey - Search key that generated the offers
   * @param {Object} clickoutData - Clickout event data
   * @param {string} clickoutData.offerId - Unique identifier for the offer (REQUIRED)
   * @param {string} [clickoutData.site] - Booking provider/website name
   * @param {number} [clickoutData.totalPrice] - Offer price
   * @param {string} [clickoutData.currency] - Price currency
   * @param {string} [clickoutData.acrissCode] - ACRISS car classification code
   * @param {string} [clickoutData.modelName] - Vehicle/accommodation model name
   * @param {Object} [clickoutData.insurances] - Insurance options
   * @param {Object} [lastCampaign] - Campaign data { name, expiry }
   * @param {Object} [searchData] - Full search data (to create missing search if needed)
   * @param {Object} searchData.params - Search parameters
   * @param {Array} [searchData.savedOffers] - Saved offer IDs
   * @param {Object} [searchData.filters] - Search filters
   * @returns {Promise<boolean>} True if successfully recorded, false otherwise
   */
  async recordClickout(userId, searchKey, clickoutData, lastCampaign = null, searchData = null) {
    try {
      // Validate required parameters
      if (!userId) {
        console.warn('ClickoutService: No userId provided for recordClickout');
        return false;
      }

      if (!searchKey) {
        console.warn('ClickoutService: No searchKey provided for recordClickout');
        return false;
      }

      if (!clickoutData?.offerId) {
        console.warn('ClickoutService: No offerId provided for recordClickout');
        return false;
      }

      // Prepare the payload
      const payload = {
        searchKey: searchKey,
        clickout: {
          offerId: clickoutData.offerId,
          timestamp: Date.now(), // Always use current timestamp
          ...(clickoutData.site && { site: clickoutData.site }),
          ...(clickoutData.totalPrice && { totalPrice: clickoutData.totalPrice }),
          ...(clickoutData.currency && { currency: clickoutData.currency }),
          ...(clickoutData.acrissCode && { acrissCode: clickoutData.acrissCode }),
          ...(clickoutData.modelName && { modelName: clickoutData.modelName }),
          ...(clickoutData.insurances && { insurances: clickoutData.insurances })
        },
        ...(lastCampaign && { lastCampaign }),
        ...(searchData && { searchData })
      };

      // Send the clickout event
      const response = await api.post(`/profile/user/${userId}/clickout`, payload);
      
      // Check if the response indicates success
      if (response.status === 200 && response.data?.success) {
        return response.data;
      }

      console.warn('ClickoutService: Unexpected response format:', response.data);
      return false;

    } catch (error) {
      // Log error but don't throw - clickout tracking should never break user experience
      console.error('ClickoutService: Failed to record clickout:', error);
      
      // Check if it's a validation error (400) vs other errors
      if (error.response?.status === 400) {
        console.error('ClickoutService: Invalid clickout data - check required fields');
      } else if (error.response?.status === 401) {
        console.error('ClickoutService: Authentication failed - user may need to log in');
      } else if (error.response?.status === 403) {
        console.error('ClickoutService: Access denied - user can only record their own clickouts');
      }

      return false;
    }
  }

  /**
   * Record multiple clickouts in batch (if needed for future features)
   * @param {string} userId - User ID from JWT token
   * @param {Array} clickouts - Array of {searchKey, clickoutData} objects
   * @param {Object} [lastCampaign] - Campaign data { name, expiry }
   * @returns {Promise<{success: number, failed: number}>} Statistics of batch operation
   */
  async recordMultipleClickouts(userId, clickouts, lastCampaign = null) {
    if (!Array.isArray(clickouts) || clickouts.length === 0) {
      return { success: 0, failed: 0 };
    }

    let success = 0;
    let failed = 0;

    // Process clickouts sequentially to avoid overwhelming the server
    for (const { searchKey, clickoutData } of clickouts) {
      const result = await this.recordClickout(userId, searchKey, clickoutData, lastCampaign);
      if (result) {
        success++;
      } else {
        failed++;
      }
    }

    return { success, failed };
  }

  /**
   * Record a clickout event for anonymous user
   * @param {string} externalClientId - External client ID for anonymous user
   * @param {string} searchKey - Search key that generated the offers
   * @param {Object} clickoutData - Clickout event data
   * @param {string} clickoutData.offerId - Unique identifier for the offer (REQUIRED)
   * @param {string} [clickoutData.site] - Booking provider/website name
   * @param {number} [clickoutData.totalPrice] - Offer price
   * @param {string} [clickoutData.currency] - Price currency
   * @param {string} [clickoutData.acrissCode] - ACRISS car classification code
   * @param {string} [clickoutData.modelName] - Vehicle/accommodation model name
   * @param {Object} [clickoutData.insurances] - Insurance options
   * @param {Object} [lastCampaign] - Campaign data { name, expiry }
   * @param {Object} [searchData] - Full search data (to create missing search if needed)
   * @returns {Promise<boolean>} True if successfully recorded, false otherwise
   */
  async recordAnonymousClickout(externalClientId, searchKey, clickoutData, lastCampaign = null, searchData = null) {
    try {
      // Validate required parameters
      if (!externalClientId) {
        console.warn('ClickoutService: No externalClientId provided for recordAnonymousClickout');
        return false;
      }

      if (!searchKey) {
        console.warn('ClickoutService: No searchKey provided for recordAnonymousClickout');
        return false;
      }

      if (!clickoutData?.offerId) {
        console.warn('ClickoutService: No offerId provided for recordAnonymousClickout');
        return false;
      }

      // Prepare the payload
      const payload = {
        searchKey: searchKey,
        clickout: {
          offerId: clickoutData.offerId,
          timestamp: Date.now(), // Always use current timestamp
          ...(clickoutData.site && { site: clickoutData.site }),
          ...(clickoutData.totalPrice && { totalPrice: clickoutData.totalPrice }),
          ...(clickoutData.currency && { currency: clickoutData.currency }),
          ...(clickoutData.acrissCode && { acrissCode: clickoutData.acrissCode }),
          ...(clickoutData.modelName && { modelName: clickoutData.modelName }),
          ...(clickoutData.insurances && { insurances: clickoutData.insurances })
        },
        ...(lastCampaign && { lastCampaign }),
        ...(searchData && { searchData })
      };

      // Send the clickout event
      const response = await api.post(`/profile/anonymous/${externalClientId}/clickout`, payload);

      // Check if the response indicates success
      if (response.status === 200 && response.data?.success) {
        return true;
      }

      console.warn('ClickoutService: Unexpected response format:', response.data);
      return false;

    } catch (error) {
      // Log error but don't throw - clickout tracking should never break user experience
      console.error('ClickoutService: Failed to record anonymous clickout:', error);

      // Check if it's a validation error (400) vs other errors
      if (error.response?.status === 400) {
        console.error('ClickoutService: Invalid clickout data - check required fields');
      }

      return false;
    }
  }
}

// Export singleton instance
const clickoutService = new ClickoutService();

// Also export the class for testing
export { ClickoutService };
export default clickoutService;
